import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { getSessionProgress } from '@/lib/utils/sessionProgress';

export interface DashboardStats {
  openDecisions: number;
  avgProgress: number;
  lastActivity: string | null;
}

export interface WorkspaceSeatInfo {
  usedSeats: number;
  maxSeats: number;
}

export interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  name: string | null;
}

export interface ActiveSession {
  id: string;
  summary: string | null;
  decision_type: string | null;
  decision_subtype: string | null;
  phase: number;
  subphase: string;
  updated_at: string;
  created_at: string;
  meta: any;
}

export interface TeamActivity {
  id: string;
  summary: string | null;
  decision_type: string | null;
  creator_name: string | null;
  updated_at: string;
  created_at: string;
}

export function useDashboardData(workspaceId: string | null) {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    openDecisions: 0,
    avgProgress: 0,
    lastActivity: null,
  });
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [teamActivities, setTeamActivities] = useState<TeamActivity[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [seatInfo, setSeatInfo] = useState<WorkspaceSeatInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user || !workspaceId) {
      setLoading(false);
      return;
    }

    async function loadDashboardData() {
      try {
        setLoading(true);

        // Fetch user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name, name')
          .eq('id', user!.id)
          .single();

        setUserProfile(profile);

        // Fetch user's active sessions
        const { data: sessions, error: sessionsError } = await supabase
          .from('decision_sessions')
          .select('*')
          .eq('created_by', user!.id)
          .eq('workspace_id', workspaceId!)
          .order('updated_at', { ascending: false });

        if (sessionsError) throw sessionsError;

        setActiveSessions(sessions || []);

        // Calculate stats
        const openSessions = sessions?.filter(s => s.phase < 3) || [];
        const lastActivity = sessions?.[0]?.updated_at || null;

        // Calculate average progress based on subphase for granular tracking
        let avgProgress = 0;
        if (openSessions.length > 0) {
          const totalProgress = openSessions.reduce((sum, session) => {
            return sum + getSessionProgress(session.subphase);
          }, 0);
          avgProgress = Math.round(totalProgress / openSessions.length);
        }

        setStats({
          openDecisions: openSessions.length,
          avgProgress,
          lastActivity,
        });

        // Fetch team activities (other workspace members)
        const { data: teamData, error: teamError } = await supabase
          .from('decision_sessions')
          .select(`
            id,
            summary,
            decision_type,
            updated_at,
            created_at,
            profiles!decision_sessions_created_by_fkey(name)
          `)
          .eq('workspace_id', workspaceId!)
          .neq('created_by', user!.id)
          .order('updated_at', { ascending: false })
          .limit(10);

        if (teamError) throw teamError;

        const activities: TeamActivity[] = (teamData || []).map((item: any) => ({
          id: item.id,
          summary: item.summary,
          decision_type: item.decision_type,
          creator_name: item.profiles?.name || null,
          updated_at: item.updated_at,
          created_at: item.created_at,
        }));

        setTeamActivities(activities);

        // Fetch seat info
        const { data: workspace } = await supabase
          .from('workspaces')
          .select('max_seats')
          .eq('id', workspaceId!)
          .single();

        const { count: activeMembers } = await supabase
          .from('workspace_members')
          .select('id', { count: 'exact', head: true })
          .eq('workspace_id', workspaceId!)
          .eq('seat_status', 'active');

        const { count: pendingInvites } = await supabase
          .from('workspace_invites')
          .select('id', { count: 'exact', head: true })
          .eq('workspace_id', workspaceId!)
          .is('accepted_at', null)
          .gt('expires_at', new Date().toISOString());

        if (workspace) {
          setSeatInfo({
            usedSeats: (activeMembers || 0) + (pendingInvites || 0),
            maxSeats: workspace.max_seats,
          });
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    }

    loadDashboardData();
  }, [user, workspaceId]);

  return {
    stats,
    activeSessions,
    teamActivities,
    userProfile,
    seatInfo,
    loading,
    error,
  };
}
